import copyToClipboard from 'copy-to-clipboard'
import { getTranslate } from 'legacy-src/utilities/i18n'
import { Suspense, useState } from 'react'

import Loader from '@/legacy-src/components/Loader'
import { Button, ButtonLink, type ButtonProps } from '@/legacy-src/components/ui/button'
import useIsAppWebView from '@/legacy-src/hooks/useIsAppWebView'
import notifyMobileApp, { NotifyAppMessages } from '@/legacy-src/utilities/notifyMobileApp'

import { type IconSizeType } from '../../types/ComponentTypes'
import Icon from '../icons/Icon'
import { getReferralPrice } from './utils/getReferralPrice'

type ShareButtonProps = {
  readonly text?: string
  readonly icon?: string
  readonly href: string
  readonly className?: string
  readonly iconSize?: IconSizeType
  readonly newWindow?: boolean
  readonly variant?: ButtonProps['variant']
}

export const ShareButton: React.FC<ShareButtonProps> = ({
  icon,
  text,
  href,
  className = '',
  iconSize,
  newWindow = false,
  variant,
}) => {
  return (
    <ButtonLink className={className} size="icon" href={href} newWindow={newWindow} variant={variant}>
      {icon && <Icon icon={icon} size={`${iconSize ?? 'sm'}`} className={`${text ? 'mr-2' : ''}`} />}
      {text}
    </ButtonLink>
  )
}

type ShareButtonsProps = {
  readonly shareData: {
    readonly code: string
    readonly noOfUses: number
  }
  readonly className?: string
  readonly alignCenter?: boolean
}

const ShareButtonsInner: React.FC<ShareButtonsProps> = ({ shareData, className }) => {
  const { t } = getTranslate()
  const isWebview = useIsAppWebView()
  const [isTextCopied, setIsTextCopied] = useState(false)

  const code = shareData?.code
  const subject = t('component.referral.mail.subject')
  const body = t('component.referral.mail.body', {
    code,
    price: getReferralPrice(),
  })

  const emailUrl = `mailto:?subject=${subject}&body=${body}`

  const buttonProps = isWebview
    ? { onClick: () => notifyMobileApp(NotifyAppMessages.OPEN_EMAIL, { email: emailUrl }), href: '#' }
    : { href: emailUrl }

  const copyText = () => {
    copyToClipboard(code)
    setIsTextCopied(true)
  }

  return (
    <div className={className}>
      <Button
        variant={isTextCopied ? 'light' : 'default'}
        className={`flex items-center uppercase`}
        size="small"
        onClick={copyText}
      >
        <Icon icon="copy" size="sm" color="white" className="mr-2" />
        {isTextCopied ? t('component.button.copied') : t('component.button.copy')}
      </Button>
      <ButtonLink className={`flex items-center uppercase`} size="small" {...buttonProps}>
        <Icon icon="mail-stroke" size="sm" color="white" className="mr-2" />
        {t('component.button.email')}
      </ButtonLink>
    </div>
  )
}

const ShareButtons: React.FC<ShareButtonsProps> = (props) => {
  return (
    <Suspense fallback={<Loader visible loaderType="section-large" />}>
      <ShareButtonsInner {...props} />
    </Suspense>
  )
}

export default ShareButtons
